import axios from "axios";

import { log } from "@/lib/log";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  headers: { "Content-Type": "application/json" },
});

api.interceptors.request.use((config) => {
  const country = detectClientCountry();
  if (country) {
    config.params = { ...(config.params || {}), country };
  }
  return config;
});

function detectClientCountry() {
  try {
    if (typeof window === "undefined") return null;
    const qp = new URLSearchParams(window.location.search).get("country");
    if (qp) {
      localStorage.setItem("riskpedia:country", qp.toUpperCase());
      return qp.toUpperCase();
    }
    const cached = localStorage.getItem("riskpedia:country");
    if (cached) return cached;
    const loc = (navigator.language || navigator.userLanguage || "").trim();
    const m = loc.match(/[-_]([A-Z]{2})\b/i);
    if (m) return m[1].toUpperCase();
  } catch (err) {
    // localStorage / navigator unavailable (private mode, SSR, sandboxed iframe).
    // Fall back to server detection (CF-IPCountry header) or US default.
    log.warn("Client country detection unavailable:", err?.message);
  }
  return null;
}

export const auth = {
  me: () => api.get("/auth/me").then((r) => r.data),
  signup: (body) => api.post("/auth/signup", body).then((r) => r.data),
  login: (body) => api.post("/auth/login", body).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  emergentSession: (sessionId) =>
    api
      .post("/auth/session", null, { headers: { "X-Session-ID": sessionId } })
      .then((r) => r.data),
};

export const finder = {
  recommend: (answers) => api.post("/finder/recommend", answers).then((r) => r.data),
  chat: (assessment_id, message) =>
    api.post("/finder/chat", { assessment_id, message }).then((r) => r.data),
};

export const assessments = {
  list: () => api.get("/assessments").then((r) => r.data),
  get: (id) => api.get(`/assessments/${id}`).then((r) => r.data),
  create: (payload) => api.post("/assessments", payload).then((r) => r.data),
  remove: (id) => api.delete(`/assessments/${id}`).then((r) => r.data),
  share: (id) => api.post(`/assessments/${id}/share`).then((r) => r.data),
  revokeShare: (id) => api.delete(`/assessments/${id}/share`).then((r) => r.data),
};

export const sharePublic = {
  get: (token) => api.get(`/share/${token}`).then((r) => r.data),
};

export const frameworks = {
  list: () => api.get("/frameworks").then((r) => r.data),
  get: (slug) => api.get(`/frameworks/${slug}`).then((r) => r.data),
};

export const admin = {
  stats: () => api.get("/admin/stats").then((r) => r.data),
  users: () => api.get("/admin/users").then((r) => r.data),
  patchUser: (id, patch) => api.patch(`/admin/users/${id}`, patch).then((r) => r.data),
  deleteUser: (id) => api.delete(`/admin/users/${id}`).then((r) => r.data),
  assessments: () => api.get("/admin/assessments").then((r) => r.data),
  createFramework: (body) => api.post("/admin/frameworks", body).then((r) => r.data),
  updateFramework: (slug, body) => api.put(`/admin/frameworks/${slug}`, body).then((r) => r.data),
  deleteFramework: (slug) => api.delete(`/admin/frameworks/${slug}`).then((r) => r.data),
  refreshFrameworks: (params) => api.post("/admin/frameworks/refresh", null, { params }).then((r) => r.data),
  // settings
  settings: () => api.get("/admin/settings").then((r) => r.data),
  saveSettings: (body) => api.put("/admin/settings", body).then((r) => r.data),
  // glossary
  glossary: (params) => api.get("/admin/glossary", { params }).then((r) => r.data),
  refreshGlossary: (params) => api.post("/admin/glossary/refresh", null, { params }).then((r) => r.data),
  createTerm: (body) => api.post("/admin/glossary", body).then((r) => r.data),
  patchTerm: (slug, body) => api.patch(`/admin/glossary/${slug}`, body).then((r) => r.data),
  deleteTerm: (slug) => api.delete(`/admin/glossary/${slug}`).then((r) => r.data),
  // tools
  refreshTools: (params) => api.post("/admin/tools/refresh", null, { params }).then((r) => r.data),
  patchTool: (slug, body) => api.patch(`/admin/tools/${slug}`, body).then((r) => r.data),
  deleteTool: (slug) => api.delete(`/admin/tools/${slug}`).then((r) => r.data),
  testAyrshare: () => api.post("/admin/ayrshare/test").then((r) => r.data),
  testBuffer: () => api.post("/admin/buffer/test").then((r) => r.data),
  introspectBuffer: () => api.post("/admin/buffer/introspect").then((r) => r.data),
  testResend: () => api.post("/admin/resend/test").then((r) => r.data),
  healthEnv: () => api.get("/health/env").then((r) => r.data),
};

export const rca = {
  fishboneSuggest: (body) => api.post("/rca/fishbone/suggest", body).then((r) => r.data),
  fiveWhysNext: (body) => api.post("/rca/five-whys/next", body).then((r) => r.data),
  fiveWhysSummary: (body) => api.post("/rca/five-whys/summary", body).then((r) => r.data),
};

export const glossary = {
  list: (params) => api.get("/glossary", { params }).then((r) => r.data),
  preview: (limit = 12) => api.get("/glossary/preview", { params: { limit } }).then((r) => r.data),
  categories: () => api.get("/glossary/categories").then((r) => r.data),
  get: (slug) => api.get(`/glossary/${slug}`).then((r) => r.data),
};

export const tools = {
  list: () => api.get("/tools").then((r) => r.data),
  get: (slug) => api.get(`/tools/${slug}`).then((r) => r.data),
  checkout: (slug, origin_url, policy_context) =>
    api.post(`/tools/${slug}/checkout`, { origin_url, ...(policy_context ? { policy_context } : {}) }).then((r) => r.data),
  razorpayVerify: (body) =>
    api.post(`/tools/razorpay/verify`, body).then((r) => r.data),
  paymentStatus: (session_id) =>
    api.get(`/payments/status/${session_id}`).then((r) => r.data),
  downloadUrl: (slug, token) => `${API}/tools/${slug}/download?token=${encodeURIComponent(token)}`,
  joinWaitlist: (slug, email) => api.post(`/tools/${slug}/waitlist`, { email }).then((r) => r.data),
};

export const publicSettings = {
  get: () => api.get("/settings/public").then((r) => r.data),
};

export const newsletters = {
  list: () => api.get("/newsletters").then((r) => r.data),
  subscribe: (email, item_ids) => api.post("/newsletters/subscribe", { email, item_ids }).then((r) => r.data),
  downloadUrl: (id) => `${API}/newsletters/${id}/download`,
  save: (id) => api.post(`/newsletters/${id}/save`).then((r) => r.data),
  unsave: (id) => api.delete(`/newsletters/${id}/save`).then((r) => r.data),
  library: () => api.get("/newsletters/my/library").then((r) => r.data),
  emailMe: (id) => api.post(`/newsletters/${id}/email-me`).then((r) => r.data),
  adminList: () => api.get("/admin/newsletters").then((r) => r.data),
  adminPatch: (id, body) => api.patch(`/admin/newsletters/${id}`, body).then((r) => r.data),
  adminSubscribers: () => api.get("/admin/newsletters/subscribers").then((r) => r.data),
  adminSendNow: (id) => api.post(`/admin/newsletters/${id}/send-now`).then((r) => r.data),
  adminGenerateAnnual: (force = false) => api.post(`/admin/newsletters/annual-report/generate?force=${force}`).then((r) => r.data),
};

export const indiaHub = {
  landing: () => api.get("/india-hub").then((r) => r.data),
  regulator: (slug) => api.get(`/india-hub/regulator/${slug}`).then((r) => r.data),
  layer: (slug, key, unlock) =>
    api.get(`/india-hub/regulator/${slug}/layer/${key}`, { params: unlock ? { unlock } : {} }).then((r) => r.data),
  cross: (slug) => api.get(`/india-hub/cross/${slug}`).then((r) => r.data),
  glossary: () => api.get("/india-hub/glossary").then((r) => r.data),
  templates: () => api.get("/india-hub/templates").then((r) => r.data),
  // Accepts either lead(email, source) or lead({email, source, name, org, role, newsletter_consent, template_id}).
  lead: (arg, source) => {
    const body = typeof arg === "string" ? { email: arg, source } : arg;
    return api.post("/india-hub/leads", body).then((r) => r.data);
  },
  requestTemplate: (id, body) => api.post(`/india-hub/templates/${id}/request`, body).then((r) => r.data),
  downloadUrl: (id, token) => `${API}/india-hub/templates/${id}/download?token=${encodeURIComponent(token)}`,
  adminScan: () => api.post("/admin/india-hub/scan").then((r) => r.data),
  adminUsage: () => api.get("/admin/india-hub/usage").then((r) => r.data),
  adminLeads: () => api.get("/admin/india-hub/leads").then((r) => r.data),
  adminSchedule: () => api.get("/admin/india-hub/schedule").then((r) => r.data),
  adminScheduleUpdate: (body) =>
    api.post("/admin/india-hub/schedule", body).then((r) => r.data),
  adminListAlerts: () => api.get("/admin/india-hub/alerts").then((r) => r.data),
  adminPublishAlert: (alertId, body) =>
    api.post(`/admin/india-hub/alerts/${alertId}/publish`, body || {}).then((r) => r.data),
  adminDismissAlert: (alertId) =>
    api.delete(`/admin/india-hub/alerts/${alertId}`).then((r) => r.data),
  recentUpdates: (slug, limit = 5) =>
    api.get(`/hub/updates/${slug}`, { params: { limit } }).then((r) => r.data),
  whatsappChannel: () => api.get("/india-hub/whatsapp-channel").then((r) => r.data),
  adminUpdateWhatsapp: (body) =>
    api.patch("/admin/india-hub/whatsapp-channel", body).then((r) => r.data),
};

export const pro = {
  plans: () => api.get("/pro/plans").then((r) => r.data),
  stats: () => api.get("/pro/stats").then((r) => r.data),
  me: () => api.get("/pro/me").then((r) => r.data),
  joinWaitlist: (body) => api.post("/pro/waitlist", body).then((r) => r.data),
  adminOverview: () => api.get("/admin/pro/overview").then((r) => r.data),
  createSubscription: (plan) =>
    api.post("/pro/razorpay/subscription", { plan }).then((r) => r.data),
  verifyPayment: (body) =>
    api.post("/pro/razorpay/verify", body).then((r) => r.data),
  cancelSubscription: () =>
    api.post("/pro/razorpay/cancel").then((r) => r.data),
};

export const jobs = {
  list: (category) =>
    api.get("/jobs", { params: category ? { category } : {} }).then((r) => r.data),
  get: (id) => api.get(`/jobs/${id}`).then((r) => r.data),
  apply: (id, body) => api.post(`/jobs/${id}/apply`, body).then((r) => r.data),
  adminList: () => api.get("/admin/job-board").then((r) => r.data),
  adminGet: (id) => api.get(`/admin/job-board/${id}`).then((r) => r.data),
  adminUpsert: (id, body) => api.put(`/admin/job-board/${id}`, body).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/job-board/${id}`).then((r) => r.data),
  adminModerate: (id, body) => api.post(`/admin/job-board/${id}/moderate`, body).then((r) => r.data),
  adminRefreshExternal: () => api.post("/admin/jobs/external/refresh").then((r) => r.data),
  // Employer self-serve
  employerList: () => api.get("/employer/jobs").then((r) => r.data),
  employerCreate: (body) => api.post("/employer/jobs", body).then((r) => r.data),
  employerPatch: (id, body) => api.patch(`/employer/jobs/${id}`, body).then((r) => r.data),
  employerClose: (id) => api.patch(`/employer/jobs/${id}/close`).then((r) => r.data),
  employerApplications: (id) => api.get(`/employer/jobs/${id}/applications`).then((r) => r.data),
};

export const verify = {
  badge: (id) => api.get(`/verify/${id}`).then((r) => r.data),
};

export const regulatorAlerts = {
  recent: (params) => api.get("/india/alerts/recent", { params }).then((r) => r.data),
  adminQueue: (params) => api.get("/admin/india/alerts/queue", { params }).then((r) => r.data),
  adminRefresh: () => api.post("/admin/india/alerts/refresh").then((r) => r.data),
  adminGenerateBrief: (id) =>
    api.post(`/admin/india/alerts/${id}/generate-brief`).then((r) => r.data),
  adminGenerateDiff: (id) =>
    api.post(`/admin/india/alerts/${id}/generate-diff`).then((r) => r.data),
  adminBulkBriefs: (params) =>
    api.post("/admin/india/alerts/bulk-generate-briefs", null, { params }).then((r) => r.data),
  adminReview: (id, body) =>
    api.post(`/admin/india/alerts/${id}/review`, body).then((r) => r.data),
};

export const reports = {
  list: () => api.get("/reports").then((r) => r.data),
  current: () => api.get("/reports/current").then((r) => r.data),
  get: (year) => api.get(`/reports/${year}`).then((r) => r.data),
  requestDownload: (year, body) =>
    api.post(`/reports/${year}/download`, body).then((r) => r.data),
  createPrintOrder: (year, body) =>
    api.post(`/reports/${year}/print-order`, body).then((r) => r.data),
  verifyPrintOrder: (body) =>
    api.post(`/reports/print-order/verify`, body).then((r) => r.data),
  // Admin
  adminList: () => api.get("/admin/reports").then((r) => r.data),
  adminUpsert: (year, body) => api.put(`/admin/reports/${year}`, body).then((r) => r.data),
  adminUploadPdf: (year, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api.post(`/admin/reports/${year}/upload-pdf`, fd, {
      headers: { "Content-Type": "multipart/form-data" },
    }).then((r) => r.data);
  },
  adminArchive: (year) => api.delete(`/admin/reports/${year}`).then((r) => r.data),
  adminDownloads: (year) => api.get(`/admin/reports/${year}/downloads`).then((r) => r.data),
  adminOrders: (year) => api.get(`/admin/reports/${year}/orders`).then((r) => r.data),
  // Campaign
  adminCampaign: (year) => api.get(`/admin/report-campaign/${year}`).then((r) => r.data),
  adminSaveCampaign: (year, body) =>
    api.put(`/admin/report-campaign/${year}`, body).then((r) => r.data),
};

export const press = {
  index: () => api.get("/press").then((r) => r.data),
  kitRequest: (body) => api.post("/press/kit-request", body).then((r) => r.data),
  adminCoverage: () => api.get("/admin/press/coverage").then((r) => r.data),
  adminAddCoverage: (body) => api.post("/admin/press/coverage", body).then((r) => r.data),
  adminRequests: () => api.get("/admin/press/requests").then((r) => r.data),
};

export const certify = {
  list: () => api.get("/certify").then((r) => r.data),
  get: (code) => api.get(`/certify/${code}`).then((r) => r.data),
  myEnrolment: (code) => api.get(`/certify/${code}/enrolment`).then((r) => r.data),
  createOrder: (code) => api.post(`/certify/${code}/enrol`).then((r) => r.data),
  verifyPayment: (body) => api.post(`/certify/enrol/verify`, body).then((r) => r.data),
  markModule: (code, n) => api.post(`/certify/${code}/modules/${n}/complete`).then((r) => r.data),
  startExam: (code) => api.post(`/certify/${code}/exam/start`).then((r) => r.data),
  submitExam: (attemptId, answers) =>
    api.post(`/certify/exam/${attemptId}/submit`, { answers }).then((r) => r.data),
  certificate: (code) => api.get(`/certify/${code}/certificate`).then((r) => r.data),
  // P4-A
  leaderboard: (params = {}) => api.get("/certify/leaderboard", { params }).then((r) => r.data),
  leaderboardMe: () => api.get("/certify/leaderboard/me").then((r) => r.data),
  leaderboardOptIn: (body) => api.post("/certify/leaderboard/opt-in", body).then((r) => r.data),
  recertEligibility: (code) => api.get(`/certify/${code}/recert/eligibility`).then((r) => r.data),
  recertOrder: (code) => api.post(`/certify/${code}/recert/order`).then((r) => r.data),
  recertVerify: (body) => api.post(`/certify/recert/verify`, body).then((r) => r.data),
  bundleOrder: () => api.post(`/certify/bundle/order`).then((r) => r.data),
  bundleVerify: (body) => api.post(`/certify/bundle/verify`, body).then((r) => r.data),
  adminList: () => api.get("/admin/certify").then((r) => r.data),
  adminReplaceModules: (code, modules) =>
    api.put(`/admin/certify/${code}/modules`, modules).then((r) => r.data),
  adminReplaceQuestions: (code, questions) =>
    api.put(`/admin/certify/${code}/questions`, questions).then((r) => r.data),
  adminEnrolments: (code) => api.get(`/admin/certify/${code}/enrolments`).then((r) => r.data),
  adminAiGenerate: (code, body) =>
    api.post(`/admin/certify/${code}/ai-generate-questions`, body).then((r) => r.data),
  adminListDrafts: (code, status = "pending") =>
    api.get(`/admin/certify/${code}/drafts`, { params: { status } }).then((r) => r.data),
  adminDecideDraft: (draftId, body) =>
    api.post(`/admin/certify/drafts/${draftId}/decide`, body).then((r) => r.data),
};

export const buildCredits = {
  list: (params) => api.get("/admin/credits", { params }).then((r) => r.data),
  log: (body) => api.post("/admin/credits", body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/credits/${id}`).then((r) => r.data),
};

export const partners = {
  landing: () => api.get("/partners").then((r) => r.data),
  submitVendor: (body) => api.post("/partners/vendors/submit", body).then((r) => r.data),
  listConsultants: (params) => api.get("/partners/consultants", { params }).then((r) => r.data),
  getConsultant: (slug) => api.get(`/partners/consultants/${slug}`).then((r) => r.data),
  joinConsultant: (body) => api.post("/partners/consultants/join", body).then((r) => r.data),
  premiumOrder: (body) => api.post("/partners/consultants/premium/order", body).then((r) => r.data),
  premiumVerify: (body) => api.post("/partners/consultants/premium/verify", body).then((r) => r.data),
  adminListVendors: (status) => api.get("/admin/partners/vendors", { params: status ? { status } : {} }).then((r) => r.data),
  adminPatchVendor: (id, body) => api.patch(`/admin/partners/vendors/${id}`, body).then((r) => r.data),
  adminDeleteVendor: (id) => api.delete(`/admin/partners/vendors/${id}`).then((r) => r.data),
  adminListConsultants: (status) => api.get("/admin/partners/consultants", { params: status ? { status } : {} }).then((r) => r.data),
  adminPatchConsultant: (id, body) => api.patch(`/admin/partners/consultants/${id}`, body).then((r) => r.data),
  adminDeleteConsultant: (id) => api.delete(`/admin/partners/consultants/${id}`).then((r) => r.data),
};

export const sponsored = {
  list: () => api.get("/sponsored").then((r) => r.data),
  get: (sponsorSlug, reportSlug) => api.get(`/sponsored/${sponsorSlug}/${reportSlug}`).then((r) => r.data),
  submitLead: (sponsorSlug, reportSlug, body) =>
    api.post(`/sponsored/${sponsorSlug}/${reportSlug}/lead`, body).then((r) => r.data),
  downloadUrl: (token) => `${API}/sponsored/download?token=${encodeURIComponent(token)}`,
  resolveDownload: (token) => api.get(`/sponsored/download`, { params: { token } }).then((r) => r.data),
  getSlot: (page) => api.get(`/sponsored/slot`, { params: { page } }).then((r) => r.data),
  trackClick: (slotId) => api.post(`/sponsored/slot/${slotId}/click`).then((r) => r.data),
  // admin
  adminListReports: () => api.get("/admin/sponsored/reports").then((r) => r.data),
  adminCreateReport: (body) => api.post("/admin/sponsored/reports", body).then((r) => r.data),
  adminPatchReport: (id, body) => api.patch(`/admin/sponsored/reports/${id}`, body).then((r) => r.data),
  adminDeleteReport: (id) => api.delete(`/admin/sponsored/reports/${id}`).then((r) => r.data),
  adminReportLeads: (id) => api.get(`/admin/sponsored/reports/${id}/leads`).then((r) => r.data),
  adminListSlots: () => api.get("/admin/sponsored/slots").then((r) => r.data),
  adminCreateSlot: (body) => api.post("/admin/sponsored/slots", body).then((r) => r.data),
  adminPatchSlot: (id, body) => api.patch(`/admin/sponsored/slots/${id}`, body).then((r) => r.data),
  adminDeleteSlot: (id) => api.delete(`/admin/sponsored/slots/${id}`).then((r) => r.data),
  adminAdvertiseLeads: () => api.get("/admin/advertise/leads").then((r) => r.data),
};

export const advertise = {
  options: () => api.get("/advertise/options").then((r) => r.data),
  submit: (body) => api.post("/advertise/inquiry", body).then((r) => r.data),
};

export const referrals = {
  options: () => api.get("/consult/options").then((r) => r.data),
  submit: (body) => api.post("/consult/intake", body).then((r) => r.data),
  // admin
  adminList: (status) => api.get("/admin/referrals", { params: status ? { status } : {} }).then((r) => r.data),
  adminGet: (id) => api.get(`/admin/referrals/${id}`).then((r) => r.data),
  adminPatch: (id, body) => api.patch(`/admin/referrals/${id}`, body).then((r) => r.data),
  adminIntroduce: (id, body) => api.post(`/admin/referrals/${id}/introduce`, body).then((r) => r.data),
  adminListFees: () => api.get("/admin/referral-fees").then((r) => r.data),
  adminCreateFee: (body) => api.post("/admin/referral-fees", body).then((r) => r.data),
  adminFeePaymentLink: (id) => api.post(`/admin/referral-fees/${id}/payment-link`).then((r) => r.data),
  adminPatchFee: (id, body) => api.patch(`/admin/referral-fees/${id}`, body).then((r) => r.data),
  adminRefreshFeeStatus: (id) => api.post(`/admin/referral-fees/${id}/refresh-status`).then((r) => r.data),
  adminDeleteFee: (id) => api.delete(`/admin/referral-fees/${id}`).then((r) => r.data),
};

export const badges = {
  // admin
  adminTargets: () => api.get("/admin/badges/targets").then((r) => r.data),
  adminList: (params) => api.get("/admin/badges/templates", { params }).then((r) => r.data),
  adminGenerate: (body) => api.post("/admin/badges/templates/generate", body).then((r) => r.data),
  adminApprove: (id) => api.post(`/admin/badges/templates/${id}/approve`).then((r) => r.data),
  adminRegenerate: (id, body) => api.post(`/admin/badges/templates/${id}/regenerate`, body || {}).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/badges/templates/${id}`).then((r) => r.data),
  // user
  mint: (body) => api.post("/badges/mint", body).then((r) => r.data),
  get: (id) => api.get(`/badges/${id}`).then((r) => r.data),
  listMine: () => api.get("/badges/me/list").then((r) => r.data),
};

export const trackerApi = {
  myKeys: () => api.get("/me/api-keys").then((r) => r.data),
  createKey: () => api.post("/me/api-keys").then((r) => r.data),
  revokeKey: (id) => api.delete(`/me/api-keys/${id}`).then((r) => r.data),
  myUsage: (days = 30) => api.get("/me/api-usage", { params: { days } }).then((r) => r.data),
  adminList: () => api.get("/admin/api-keys").then((r) => r.data),
  adminMint: (body) => api.post("/admin/api-keys", body).then((r) => r.data),
  adminRevoke: (id) => api.post(`/admin/api-keys/${id}/revoke`).then((r) => r.data),
  adminSetPlan: (id, plan) => api.patch(`/admin/api-keys/${id}/plan`, { plan }).then((r) => r.data),
  adminUsage: () => api.get("/admin/api-usage").then((r) => r.data),
};

export const forum = {
  settings: () => api.get("/forum/settings").then((r) => r.data),
  listThreads: () => api.get("/forum/threads").then((r) => r.data),
  getThread: (id) => api.get(`/forum/threads/${id}`).then((r) => r.data),
  createThread: (body) => api.post("/forum/threads", body).then((r) => r.data),
  reply: (id, body) => api.post(`/forum/threads/${id}/replies`, body).then((r) => r.data),
  summarize: (id, force = false) =>
    api.post(`/forum/threads/${id}/summarize${force ? "?force=1" : ""}`).then((r) => r.data),
  adminToggle: (enabled) =>
    api.patch("/admin/forum/settings", { enabled }).then((r) => r.data),
  adminDeleteThread: (id) =>
    api.delete(`/admin/forum/threads/${id}`).then((r) => r.data),
  adminDeleteReply: (threadId, replyId) =>
    api.delete(`/admin/forum/threads/${threadId}/replies/${replyId}`).then((r) => r.data),
};

export const grc = {
  monitorSettings: () => api.get("/grc/monitor/settings").then((r) => r.data),
  adminToggleMonitor: (enabled) =>
    api.patch("/admin/grc/monitor/settings", { enabled }).then((r) => r.data),
  startSession: (body) => api.post("/grc/monitor/sessions", body).then((r) => r.data),
  snapshot: (sessionId, thumb_b64) =>
    api.post(`/grc/monitor/sessions/${sessionId}/snapshot`, { thumb_b64 }).then((r) => r.data),
  completeSession: (sessionId, body = {}) =>
    api.post(`/grc/monitor/sessions/${sessionId}/complete`, body).then((r) => r.data),
  adminListSessions: () => api.get("/admin/grc/monitor/sessions").then((r) => r.data),
  adminSnapshots: (sessionId) =>
    api.get(`/admin/grc/monitor/sessions/${sessionId}/snapshots`).then((r) => r.data),
  salesTriggers: (audience = "both") =>
    api.get("/grc/sales-triggers", { params: { audience } }).then((r) => r.data),
};

export const adminCaps = {
  get: () => api.get("/admin/caps").then((r) => r.data),
};

export const adminSeo = {
  publishToAi: (paths) =>
    api.post("/admin/seo/publish-to-ai", paths ? { paths } : {}).then((r) => r.data),
  indexnowKey: () => api.get("/seo/indexnow-key").then((r) => r.data),
  stats: () => api.get("/seo/stats").then((r) => r.data),
  health: () => api.get("/admin/seo/health").then((r) => r.data),
  indexnowPing: (paths) =>
    api.post("/seo/indexnow-ping", { paths }).then((r) => r.data),
  bumpOgVersion: () => api.post("/admin/seo/bump-og-version").then((r) => r.data),
  ogVersion: () => api.get("/seo/og-version").then((r) => r.data),
};

export const preferences = {
  get: (email, token) =>
    api.get("/preferences", { params: { email, token } }).then((r) => r.data),
  update: (email, token, item_ids) =>
    api.post("/preferences/update", { email, token, item_ids }).then((r) => r.data),
  unsubscribeAll: (email, token, purge_lead = false) =>
    api.post("/preferences/unsubscribe-all", { email, token, purge_lead }).then((r) => r.data),
};

export const aiCitations = {
  list: (limit = 6) => api.get("/ai-citations", { params: { limit } }).then((r) => r.data),
  adminList: () => api.get("/admin/ai-citations").then((r) => r.data),
  create: (body) => api.post("/admin/ai-citations", body).then((r) => r.data),
  update: (id, body) => api.patch(`/admin/ai-citations/${id}`, body).then((r) => r.data),
  remove: (id) => api.delete(`/admin/ai-citations/${id}`).then((r) => r.data),
};

export const india = {
  list: (params) => api.get("/india", { params }).then((r) => r.data),
  regulators: () => api.get("/india/regulators").then((r) => r.data),
  trending: () => api.get("/india/trending").then((r) => r.data),
  get: (slug) => api.get(`/india/${slug}`).then((r) => r.data),
  adminRefresh: (limit = 5) =>
    api.post("/admin/india/refresh", null, { params: { limit } }).then((r) => r.data),
  adminTrendingReport: (force = false) =>
    api.get("/admin/india/trending-report", { params: { force } }).then((r) => r.data),
  adminGrowTrending: (limit = 5) =>
    api.post("/admin/india/grow-trending", null, { params: { limit } }).then((r) => r.data),
  adminDelete: (slug) => api.delete(`/admin/india/${slug}`).then((r) => r.data),
};

export const carouselLite = {
  status: () => api.get("/carousel-lite/status").then((r) => r.data),
  pricing: () => api.get("/carousel-lite/pricing").then((r) => r.data),
  checkout: (origin_url, plan = "lite") => api.post("/carousel-lite/checkout", { origin_url, plan }).then((r) => r.data),
  paymentStatus: (session_id) => api.get(`/carousel-lite/payment-status/${session_id}`).then((r) => r.data),
  generate: (body) => api.post("/carousel-lite/generate", body).then((r) => r.data),
  history: () => api.get("/carousel-lite/history").then((r) => r.data),
  razorpayOrder: (plan = "lite") => api.post("/carousel-lite/razorpay/order", { plan }).then((r) => r.data),
  razorpayVerify: (body) => api.post("/carousel-lite/razorpay/verify", body).then((r) => r.data),
};

export const indiaStates = {
  list: () => api.get("/india/states").then((r) => r.data),
};

export const socialQueue = {
  list: (status) => api.get("/admin/social/queue", { params: status ? { status } : {} }).then((r) => r.data),
  updateStatus: (postId, body) => api.patch(`/admin/social/posts/${postId}/status`, body).then((r) => r.data),
  updateCopy: (postId, body) => api.patch(`/admin/social/posts/${postId}/copy`, body).then((r) => r.data),
  publish: (postId) => api.post(`/admin/social/posts/${postId}/publish`).then((r) => r.data),
  republish: (postId) => api.post(`/admin/social/posts/${postId}/republish`).then((r) => r.data),
  republishBulk: (postIds) => api.post("/admin/social/republish-bulk", { post_ids: postIds }).then((r) => r.data),
  delete: (postId) => api.delete(`/admin/social/posts/${postId}`).then((r) => r.data),
  refreshAnalytics: (postId) => api.post(`/admin/social/posts/${postId}/refresh-analytics`).then((r) => r.data),
  refreshAllAnalytics: (days = 30) => api.post("/admin/social/analytics/refresh-all", null, { params: { days } }).then((r) => r.data),
  topPerforming: (days = 30, limit = 5) => api.get("/admin/social/analytics/top", { params: { days, limit } }).then((r) => r.data),
  weeklyInsights: () => api.get("/admin/social/insights/weekly").then((r) => r.data),
  fallbackEvents: (limit = 5) => api.get("/admin/social/fallback-events", { params: { limit } }).then((r) => r.data),
  bulkPromoteRoadmap: (body = {}) => api.post("/admin/social/bulk-promote-roadmap", body).then((r) => r.data),
};

export const riskRegister = {
  workspaces: () => api.get("/risk-register/workspaces").then((r) => r.data),
  createWorkspace: (body) => api.post("/risk-register/workspaces", body).then((r) => r.data),
  invite: (workspaceId, email) =>
    api.post(`/risk-register/workspaces/${workspaceId}/invite`, { email }).then((r) => r.data),
  removeMember: (workspaceId, userId) =>
    api.delete(`/risk-register/workspaces/${workspaceId}/members/${userId}`).then((r) => r.data),
  rows: (workspaceId) =>
    api.get("/risk-register/rows", { params: workspaceId ? { workspace_id: workspaceId } : {} }).then((r) => r.data),
  addRow: (body, workspaceId) =>
    api
      .post("/risk-register/rows", body, { params: workspaceId ? { workspace_id: workspaceId } : {} })
      .then((r) => r.data),
  patchRow: (rowId, patch) =>
    api.patch(`/risk-register/rows/${rowId}`, patch).then((r) => r.data),
  deleteRow: (rowId) => api.delete(`/risk-register/rows/${rowId}`).then((r) => r.data),
};

export const workspaces = {
  list: () => api.get("/workspaces").then((r) => r.data),
  create: (name) => api.post("/workspaces", { name }).then((r) => r.data),
  invite: (workspaceId, email) =>
    api.post(`/workspaces/${workspaceId}/invite`, { email }).then((r) => r.data),
  removeMember: (workspaceId, userId) =>
    api.delete(`/workspaces/${workspaceId}/members/${userId}`).then((r) => r.data),
  remove: (workspaceId) => api.delete(`/workspaces/${workspaceId}`).then((r) => r.data),
};

export const savedTemplates = {
  list: (params = {}) => api.get("/saved-templates", { params }).then((r) => r.data),
  create: (body) => api.post("/saved-templates", body).then((r) => r.data),
  get: (saveId) => api.get(`/saved-templates/${saveId}`).then((r) => r.data),
  update: (saveId, patch) => api.put(`/saved-templates/${saveId}`, patch).then((r) => r.data),
  remove: (saveId) => api.delete(`/saved-templates/${saveId}`).then((r) => r.data),
};

export const chatbot = {
  personas: () => api.get("/chatbot/personas").then((r) => r.data),
  send: (body) => api.post("/chatbot/send", body).then((r) => r.data),
  click: (body) => api.post("/chatbot/click", body).then((r) => r.data),
  adminListKb: (params = {}) => api.get("/admin/chatbot/kb", { params }).then((r) => r.data),
  adminCreateKb: (body) => api.post("/admin/chatbot/kb", body).then((r) => r.data),
  adminPatchKb: (id, patch) => api.patch(`/admin/chatbot/kb/${id}`, patch).then((r) => r.data),
  adminDeleteKb: (id) => api.delete(`/admin/chatbot/kb/${id}`).then((r) => r.data),
  adminSeedKb: () => api.post("/admin/chatbot/kb/seed").then((r) => r.data),
  adminExportKb: (fmt = "csv", persona) => {
    const params = new URLSearchParams({ fmt });
    if (persona) params.set("persona", persona);
    return `${API}/admin/chatbot/kb/export?${params.toString()}`;
  },
  adminListLogs: (params = {}) => api.get("/admin/chatbot/logs", { params }).then((r) => r.data),
  adminPromoteLog: (body) => api.post("/admin/chatbot/logs/promote", body).then((r) => r.data),
  adminDeleteLog: (id) => api.delete(`/admin/chatbot/logs/${id}`).then((r) => r.data),
  adminClicks: (limit = 50) => api.get("/admin/chatbot/clicks", { params: { limit } }).then((r) => r.data),
  adminListPersonas: () => api.get("/admin/chatbot/personas").then((r) => r.data),
  adminCreatePersona: (body) => api.post("/admin/chatbot/personas", body).then((r) => r.data),
  adminPatchPersona: (id, patch) => api.patch(`/admin/chatbot/personas/${id}`, patch).then((r) => r.data),
  adminDeletePersona: (id) => api.delete(`/admin/chatbot/personas/${id}`).then((r) => r.data),
};

export const enterprise = {
  teamStarterOrder: (body) => api.post("/enterprise/team-starter/order", body).then((r) => r.data),
  teamStarterVerify: (body) => api.post("/enterprise/team-starter/verify", body).then((r) => r.data),
  myOrg: () => api.get("/enterprise/me/org").then((r) => r.data),
  patchOrg: (patch) => api.patch("/enterprise/me/org", patch).then((r) => r.data),
  inviteMember: (body) => api.post("/enterprise/me/org/members", body).then((r) => r.data),
  removeMember: (email) => api.delete(`/enterprise/me/org/members/${encodeURIComponent(email)}`).then((r) => r.data),
  listObligations: () => api.get("/enterprise/me/obligations").then((r) => r.data),
  addObligation: (body) => api.post("/enterprise/me/obligations", body).then((r) => r.data),
  patchObligation: (id, body) => api.patch(`/enterprise/me/obligations/${id}`, body).then((r) => r.data),
  deleteObligation: (id) => api.delete(`/enterprise/me/obligations/${id}`).then((r) => r.data),
  adminListOrgs: () => api.get("/admin/enterprise/orgs").then((r) => r.data),
};

export const reseller = {
  apply: (body) => api.post("/reseller/apply", body).then((r) => r.data),
  validateCode: (code) => api.get(`/reseller/code/${encodeURIComponent(code)}`).then((r) => r.data),
  myDashboard: () => api.get("/me/reseller").then((r) => r.data),
  adminApplications: (status) => api.get("/admin/reseller/applications", { params: status ? { status } : {} }).then((r) => r.data),
  adminDecide: (appId, body) => api.post(`/admin/reseller/applications/${appId}/decide`, body).then((r) => r.data),
  adminCodes: () => api.get("/admin/reseller/codes").then((r) => r.data),
  adminSales: (params = {}) => api.get("/admin/reseller/sales", { params }).then((r) => r.data),
  adminPayout: (body) => api.post("/admin/reseller/payouts", body).then((r) => r.data),
};

// P4-F — YouTube Shorts programme
export const youtube = {
  list: (params = {}) => api.get("/youtube", { params }).then((r) => r.data),
  adminList: () => api.get("/admin/youtube").then((r) => r.data),
  adminCreate: (body) => api.post("/admin/youtube", body).then((r) => r.data),
  adminPatch: (videoId, body) => api.patch(`/admin/youtube/${videoId}`, body).then((r) => r.data),
  adminDelete: (videoId) => api.delete(`/admin/youtube/${videoId}`).then((r) => r.data),
  adminShareLog: () => api.get("/admin/youtube/share-log").then((r) => r.data),
};

// P2 — Speakers & Consulting referral network
export const speakers = {
  list: (params = {}) => api.get("/speakers", { params }).then((r) => r.data),
  get: (slug) => api.get(`/speakers/${slug}`).then((r) => r.data),
  stats: () => api.get("/speakers/stats").then((r) => r.data),
  book: (slug, body) => api.post(`/speakers/${slug}/book`, body).then((r) => r.data),
  myProfile: () => api.get("/speakers/me/profile").then((r) => r.data),
  upsertMyProfile: (body) => api.post("/speakers/me/profile", body).then((r) => r.data),
  myBookings: () => api.get("/speakers/me/bookings").then((r) => r.data),
  respondBooking: (bookingId, body) => api.patch(`/speakers/me/bookings/${bookingId}`, body).then((r) => r.data),
  // Boost
  boostConfigPublic: () => api.get("/speakers/boost/config").then((r) => r.data),
  boostCheckout: () => api.post("/speakers/me/boost/checkout").then((r) => r.data),
  boostRefreshStatus: () => api.post("/speakers/me/boost/refresh-status").then((r) => r.data),
  // Admin
  adminList: () => api.get("/admin/speakers").then((r) => r.data),
  adminPatch: (id, body) => api.patch(`/admin/speakers/${id}`, body).then((r) => r.data),
  adminBookings: () => api.get("/admin/speakers/bookings").then((r) => r.data),
  adminBoostConfig: () => api.get("/admin/speakers/boost/config").then((r) => r.data),
  adminPutBoostConfig: (body) => api.put("/admin/speakers/boost/config", body).then((r) => r.data),
  adminGrantBoost: (id, body) => api.post(`/admin/speakers/${id}/grant-boost`, body).then((r) => r.data),
  adminRevokeBoost: (id) => api.post(`/admin/speakers/${id}/revoke-boost`).then((r) => r.data),
  adminBoostOrders: () => api.get("/admin/speakers/boost/orders").then((r) => r.data),
};

export const webinars = {
  list: () => api.get("/webinars").then((r) => r.data),
  get: (slug) => api.get(`/webinars/${slug}`).then((r) => r.data),
  register: (webinarId, body) => api.post(`/webinars/${webinarId}/register`, body).then((r) => r.data),
  icsUrl: (webinarId) => `${API}/webinars/${webinarId}/ics`,
  adminList: () => api.get("/admin/webinars").then((r) => r.data),
  adminCreate: (body) => api.post("/admin/webinars", body).then((r) => r.data),
  adminPatch: (id, body) => api.patch(`/admin/webinars/${id}`, body).then((r) => r.data),
  adminDelete: (id) => api.delete(`/admin/webinars/${id}`).then((r) => r.data),
  adminRegistrations: (id) => api.get(`/admin/webinars/${id}/registrations`).then((r) => r.data),
  adminSendRecording: (id) => api.post(`/admin/webinars/${id}/send-recording`).then((r) => r.data),
  adminListGuests: () => api.get("/admin/webinars/guests").then((r) => r.data),
  adminCreateGuest: (body) => api.post("/admin/webinars/guests", body).then((r) => r.data),
  adminPatchGuest: (id, body) => api.patch(`/admin/webinars/guests/${id}`, body).then((r) => r.data),
  adminDeleteGuest: (id) => api.delete(`/admin/webinars/guests/${id}`).then((r) => r.data),
};



// Help Section — articles, search, regulatory map, changelog
export const help = {
  list: (params = {}) => api.get("/help/articles", { params }).then((r) => r.data),
  get: (slug) => api.get(`/help/articles/${slug}`).then((r) => r.data),
  search: (q) => api.get("/help/search", { params: { q } }).then((r) => r.data),
  regulatory: () => api.get("/help/regulatory").then((r) => r.data),
  changelog: (params = {}) => api.get("/help/changelog", { params }).then((r) => r.data),
  // admin
  adminList: () => api.get("/admin/help/articles").then((r) => r.data),
  adminCreate: (body) => api.post("/admin/help/articles", body).then((r) => r.data),
  adminUpdate: (slug, body) => api.put(`/admin/help/articles/${slug}`, body).then((r) => r.data),
  adminDelete: (slug) => api.delete(`/admin/help/articles/${slug}`).then((r) => r.data),
  adminSeed: () => api.post("/admin/help/seed").then((r) => r.data),
};

// User preferences (nav order, dismissed suggestions) + activity tracking + next-step suggestions
export const userPrefs = {
  get: () => api.get("/me/preferences").then((r) => r.data),
  update: (body) => api.put("/me/preferences", body).then((r) => r.data),
  track: (event_type, ref = "") =>
    api.post("/me/activity", { event_type, ref }).then((r) => r.data).catch(() => null),
  nextStep: () => api.get("/me/next-step").then((r) => r.data),
};

// Phase C — Enterprise team invites + shared workflows.
export const teams = {
  me: () => api.get("/teams/me").then((r) => r.data),
  create: (name) => api.post("/teams", { name }).then((r) => r.data),
  invite: (team_id, email, role = "member") =>
    api.post(`/teams/${team_id}/invites`, { email, role }).then((r) => r.data),
  removeMember: (team_id, membership_id) =>
    api.delete(`/teams/${team_id}/members/${membership_id}`).then((r) => r.data),
  disband: (team_id) => api.delete(`/teams/${team_id}`).then((r) => r.data),
  accept: (membership_id) =>
    api.post("/team-invites/accept", { membership_id }).then((r) => r.data),
  decline: (membership_id) =>
    api.post("/team-invites/decline", { membership_id }).then((r) => r.data),
  share: (workflow_id, team_id) =>
    api.patch(`/automation/my-workflows/${workflow_id}/share`, { team_id }).then((r) => r.data),
};
export const automation = {
  listTemplates: (params = {}) => api.get("/automation/templates", { params }).then((r) => r.data),
  getTemplate: (slug) => api.get(`/automation/templates/${slug}`).then((r) => r.data),
  getTemplateSample: (slug) => api.get(`/automation/templates/${slug}/sample`).then((r) => r.data),
  myRecentRunsForTemplate: (slug, limit = 3) =>
    api.get(`/automation/templates/${slug}/my-recent-runs`, { params: { limit } }).then((r) => r.data),
  saveWorkflow: (body) => api.post("/automation/my-workflows", body).then((r) => r.data),
  listMyWorkflows: () => api.get("/automation/my-workflows").then((r) => r.data),
  patchWorkflow: (id, patch) => api.patch(`/automation/my-workflows/${id}`, patch).then((r) => r.data),
  deleteWorkflow: (id) => api.delete(`/automation/my-workflows/${id}`).then((r) => r.data),
  setSchedule: (id, body) => api.put(`/automation/my-workflows/${id}/schedule`, body).then((r) => r.data),
  runNow: (id) => api.post(`/automation/my-workflows/${id}/run`).then((r) => r.data),
  listRuns: (id) => api.get(`/automation/my-workflows/${id}/runs`).then((r) => r.data),
  getRun: (id, run_id) => api.get(`/automation/my-workflows/${id}/runs/${run_id}`).then((r) => r.data),
  riskCultureSurvey: () => api.get("/automation/risk-culture/survey").then((r) => r.data),
  cyberFrameworkQuestions: () => api.get("/automation/cyber-framework/questions").then((r) => r.data),
  // F04 — SEBI LODR Reg 30 event catalogue (Para A + Para B with SLA hours)
  lodrEvents: () => api.get("/automation/sebi-lodr/events").then((r) => r.data),
  // F06 — SEBI SCORES grievance categories
  scoresCategories: () => api.get("/automation/sebi-scores/categories").then((r) => r.data),
  // Public trending — top-N most-shared templates over the trailing N days
  trending: (days = 30, limit = 3) =>
    api.get("/automation/trending", { params: { days, limit } }).then((r) => r.data),
  // F10 — ICAI audit-quality 20-indicator catalog
  icaiIndicators: () => api.get("/automation/icai-audit/indicators").then((r) => r.data),
  // F11 — AML RBA reference options (products, channels, geos, weights)
  amlRbaOptions: () => api.get("/automation/aml-rba/options").then((r) => r.data),
  // DPDP scorecard (public lead-gen)
  dpdpScorecardQuestions: () => api.get("/automation/dpdp-scorecard/questions").then((r) => r.data),
  dpdpScorecardScore: (body) => api.post("/automation/dpdp-scorecard/score", body).then((r) => r.data),
  dpdpScorecardStats: () => api.get("/automation/dpdp-scorecard/stats").then((r) => r.data),
  // Admin refresh
  refreshStatus: () => api.get("/admin/automation/refresh-status").then((r) => r.data),
  refreshModels: () => api.get("/admin/automation/refresh-models").then((r) => r.data),
  refreshPreview: (slugs, model_id) => api.post("/admin/automation/refresh-preview", { slugs, model_id }).then((r) => r.data),
  refreshApply: (slug, refreshed_template, model_label) =>
    api.post("/admin/automation/refresh-apply", { slug, refreshed_template, model_label }).then((r) => r.data),
  stageTrending: (idea) => api.post("/admin/automation/stage-trending", idea).then((r) => r.data),
  getRefreshCron: () => api.get("/admin/automation/refresh-cron").then((r) => r.data),
  setRefreshCron: (body) => api.put("/admin/automation/refresh-cron", body).then((r) => r.data),
  // Admin — F12 share-attribution viewer (LinkedIn / copy / twitter / other clicks per run)
  shareAttribution: (limit = 100) =>
    api.get("/admin/automation/share-attribution", { params: { limit } }).then((r) => r.data),
  certificateUrl: (id, run_id) => `${API}/automation/my-workflows/${id}/runs/${run_id}/certificate.pdf`,
  recordShareClick: (run_id, channel) =>
    api.post(`/automation/runs/${run_id}/share-click`, { channel }).then((r) => r.data).catch(() => null),
  creditsMeter: () => api.get("/automation/my-credits").then((r) => r.data),
};

export const indiaRiskPulse = {
  // Public
  current: () => api.get("/india-risk-pulse/current").then((r) => r.data),
  editions: (limit = 24) => api.get("/india-risk-pulse/editions", { params: { limit } }).then((r) => r.data),
  edition: (tag) => api.get(`/india-risk-pulse/editions/${tag}`).then((r) => r.data),
  history: (limit = 12) => api.get("/india-risk-pulse/history", { params: { limit } }).then((r) => r.data),
  // Admin
  adminSettings: () => api.get("/admin/india-risk-pulse/settings").then((r) => r.data),
  adminUpdateSettings: (patch) => api.put("/admin/india-risk-pulse/settings", patch).then((r) => r.data),
  adminGenerate: (body = {}) => api.post("/admin/india-risk-pulse/generate-draft", body).then((r) => r.data),
  adminDrafts: () => api.get("/admin/india-risk-pulse/drafts").then((r) => r.data),
  adminDraft: (editionId) => api.get(`/admin/india-risk-pulse/drafts/${editionId}`).then((r) => r.data),
  adminDiff: (editionId) => api.get(`/admin/india-risk-pulse/drafts/${editionId}/diff`).then((r) => r.data),
  adminApprove: (editionId) => api.post(`/admin/india-risk-pulse/drafts/${editionId}/approve`).then((r) => r.data),
  adminReject: (editionId, reason) =>
    api.post(`/admin/india-risk-pulse/drafts/${editionId}/reject`, { reason }).then((r) => r.data),
  // Generates a real Claude-drafted social post for a chosen IRP domain by
  // calling the Social Composer in the background. The draft lands in the
  // Social Composer drafts list under the `india_risk_pulse` tab.
  //
  // Pass BOTH editionId and editionTag — editionId is the stable lookup key
  // (works for draft + published editions), editionTag is the human-readable
  // fallback. The backend tries edition_id first, then edition_tag, then the
  // latest published edition (per `_pick_india_risk_pulse`).
  adminComposeSocial: (editionTag, domainId, options = {}, editionId = null) =>
    api.post("/admin/social/generate", {
      content_type: "india_risk_pulse",
      subject_kind: "domain",
      subject_slug: editionTag,
      subject_extra: { domain_id: domainId, ...(editionId ? { edition_id: editionId } : {}) },
      include_image: options.include_image ?? true,
      image_model: options.image_model || "gpt-image-1",
      include_video: options.include_video ?? false,
      video_provider: options.video_provider || "sora",
      video_duration: options.video_duration ?? 8,
      include_carousel: options.include_carousel ?? false,
      carousel_platforms: options.carousel_platforms || ["instagram", "linkedin"],
      carousel_provider: options.carousel_provider || "in_house",
      carousel_image_model: options.carousel_image_model || "gemini-3.1-flash-image-preview",
    }).then((r) => r.data),
  // Embed lead-capture + admin dashboard
  submitEmbedRequest: (body) => api.post("/india-risk-pulse/embed-request", body).then((r) => r.data),
  adminListEmbedRequests: (status = null) =>
    api.get("/admin/india-risk-pulse/embed-requests", { params: status ? { status } : {} }).then((r) => r.data),
  adminUpdateEmbedRequest: (id, patch) =>
    api.patch(`/admin/india-risk-pulse/embed-requests/${id}`, patch).then((r) => r.data),
  adminWidgetLoads: (limit = 50) =>
    api.get("/admin/india-risk-pulse/widget-loads", { params: { limit } }).then((r) => r.data),
};

// ── Site content (features + what's-new) ──────────────────────────────────
export const siteContent = {
  features: () => api.get("/site/features").then((r) => r.data),
  whatsNew: () => api.get("/site/whats-new").then((r) => r.data),
  // Admin
  adminEmailWhatsNew: (body) => api.post("/admin/site/whats-new/email", body).then((r) => r.data),
  adminListSends: (limit = 30) =>
    api.get("/admin/site/whats-new/sends", { params: { limit } }).then((r) => r.data),
  adminListUsers: () => api.get("/admin/users").then((r) => r.data),
};


